/**
 * DashboardTabs — iter87.0: repurposed from the tab strip into the
 * PLANE container for the bento home. Each tile on BentoHome opens
 * one plane here inside a Takeover (full-screen + back chevron on
 * mobile, wide centered panel on desktop).
 *
 * iter87.5 — The "My Picks" plane is retired: every pick/boost
 * activity now lives in its own chip-driven pop-up modal
 * (PotPicksModal / MatchdayBoostModal / PowerPickModal /
 * KnockoutPickModal / RankTeamsModal). The `picksContent`,
 * `useMatchdayBoost` and `boostsOpen` scaffolding are gone.
 */
import Takeover from "@/components/onui/Takeover";
import BracketView from "@/components/BracketView";
import GroupStandings from "@/components/GroupStandings";
import { ScheduleWidget } from "@/components/ScheduleWidget";
import SquaresGameProgress from "@/components/dashboard/SquaresGameProgress";
import LeaderboardView from "@/components/dashboard/LeaderboardView";
import GolfLeaderboard from "@/components/dashboard/GolfLeaderboard";
import GolfStandings from "@/components/dashboard/GolfStandings";
import NFLStandings from "@/components/dashboard/NFLStandings";
import MyTeamsGrid from "@/components/dashboard/MyTeamsGrid";
import MlbPlayoffStandings from "@/components/dashboard/MlbPlayoffStandings";
import NbaPlayoffStandings from "@/components/dashboard/NbaPlayoffStandings";
import NwslPlayoffStandings from "@/components/dashboard/NwslPlayoffStandings";
import MlsPlayoffStandings from "@/components/dashboard/MlsPlayoffStandings";
import UclPlayoffStandings from "@/components/dashboard/UclPlayoffStandings";
import FootballPlayoffStandings from "@/components/dashboard/FootballPlayoffStandings";
import { isFootballPlayoffsTemplate } from "@/lib/footballPlayoffsTemplates";
import { isGolfFormat } from "@/lib/tournamentFormat";
import { Trophy, Calendar, List, GitBranch, Users, Activity } from "lucide-react";

const TITLES = {
    leaderboard: "Leaderboard",
    schedule: "Schedule",
    "game-progress": "Game Progress",
    standings: "Standings",
    bracket: "Bracket",
    teams: "My Teams",
};

// iter87.17 — Draft-style header per plane: icon tile + tone.
// Every Cheer tile inherits the same visual language as Pot Picks /
// Power Up / Matchday Boost modals (icon square + title + chevron-down
// close + tone-tinted outer ring).
const HEAD = {
    leaderboard: { icon: Trophy,    tone: "cyber" },
    schedule:    { icon: Calendar,  tone: "cyber" },
    "game-progress": { icon: Activity, tone: "heat" },
    standings:   { icon: List,      tone: "cyber" },
    bracket:     { icon: GitBranch, tone: "cyber" },
    teams:       { icon: Users,     tone: "cyber" },
};

export default function DashboardTabs({
    view,
    onClose,
    leagueId,
    tournament,
    data,
    me,
    board,
    myRow,
    myTeams,
    isSpectator,
    onTeamRenamed,
}) {
    // iter86.31 — Golf: no fixtures/schedule, no bracket.
    const isGolf = isGolfFormat(tournament);
    const head = HEAD[view] || {};

    return (
        <Takeover
            open={!!view}
            onClose={onClose}
            title={TITLES[view] || ""}
            testId="dashboard-plane"
            maxWidthClass="lg:max-w-5xl"
            icon={head.icon}
            iconTone={head.tone}
        >
            {view === "leaderboard" && (
                <div className="space-y-4" data-testid="pane-leaderboard">
                    {isGolf ? (
                        <GolfLeaderboard
                            board={board}
                            tournament={tournament}
                            meId={me?.id}
                            isAdmin={!!me?.is_admin}
                            onSynced={onTeamRenamed}
                        />
                    ) : (
                        <LeaderboardView
                            board={board}
                            tournament={tournament}
                            meId={me?.id}
                        />
                    )}
                </div>
            )}

            {/* iter87.5 — Picks plane retired. Every pick/boost activity
                is now reached through a To-Do chip that opens its OWN
                focused Takeover / Dialog. No shared stacking surface. */}

            {view === "bracket" && (
                <div className="space-y-3" data-testid="pane-bracket">
                    {tournament?.template === "mlb_playoffs_api" ? (
                        <MlbPlayoffStandings tournamentId={tournament?.id} teams={tournament?.teams || []} />
                    ) : tournament?.template === "nba_playoffs_api" ? (
                        <NbaPlayoffStandings tournamentId={tournament?.id} teams={tournament?.teams || []} />
                    ) : tournament?.template === "nwsl_playoffs_api" ? (
                        <NwslPlayoffStandings tournamentId={tournament?.id} />
                    ) : tournament?.template === "mls_playoffs_api" ? (
                        <MlsPlayoffStandings tournamentId={tournament?.id} teams={(tournament?.mls || {}).teams || []} />
                    ) : tournament?.template === "ucl_playoffs_api" ? (
                        <UclPlayoffStandings tournamentId={tournament?.id} teams={(tournament?.ucl || {}).teams || []} />
                    ) : isFootballPlayoffsTemplate(tournament?.template) ? (
                        <FootballPlayoffStandings tournamentId={tournament?.id} template={tournament?.template} teams={tournament?.teams || []} />
                    ) : (
                        <>
                    {/* iter84.57.4 — Auto-materialize failure hint (recoverable
                        via manual import from Admin → Live Sync). */}
                    {tournament?.materialization_state === "failed" && (
                        <div
                            data-testid="materialization-failed-banner"
                            className="p-3 border border-rose-500/40 bg-rose-500/5 rounded-xl text-xs leading-relaxed"
                        >
                            <p className="font-bold uppercase tracking-wider text-rose-400 mb-1">
                                Initial fixtures import did not complete
                            </p>
                            <p className="text-zinc-300">
                                Tournament was created, but the auto-import from API-Football couldn't fetch the bracket.
                                Open <b>Admin → Live Sync → API-Football</b> and click <b>Import</b> to populate it manually.
                            </p>
                            {tournament?.materialization_error && (
                                <p className="text-[10px] text-zinc-500 font-mono mt-1">
                                    {tournament.materialization_error}
                                </p>
                            )}
                        </div>
                    )}
                    {tournament?.materialization_state === "pending" && (
                        <div
                            data-testid="materialization-pending-banner"
                            className="p-3 border border-amber-500/40 bg-amber-500/5 rounded-xl text-xs leading-relaxed"
                        >
                            <p className="font-bold uppercase tracking-wider text-amber-400 mb-1">
                                Importing fixtures…
                            </p>
                            <p className="text-zinc-300">
                                The bracket is being populated from API-Football — refresh in a few seconds.
                            </p>
                        </div>
                    )}
                    <BracketView
                        tournament={tournament}
                        matches={data.matches || []}
                        myTeamIds={me?.assigned_team_ids || []}
                        participants={data.participants || []}
                    />
                        </>
                    )}
                </div>
            )}

            {view === "standings" && (
                <div data-testid="pane-standings">
                    {isGolf ? (
                        <GolfStandings
                            tournament={tournament}
                            myTeamIds={(board.find(r => r.participant_id === me?.id)?.team_breakdown || []).map(tb => tb.team_id)}
                        />
                    ) : (tournament?.template === "nfl_season_api"
                            || tournament?.template === "nfl_boost_api") ? (
                        <NFLStandings
                            tournament={tournament}
                            myTeamIds={me?.assigned_team_ids || []}
                        />
                    ) : (
                        <GroupStandings
                            tournament={tournament}
                            myTeamIds={me?.assigned_team_ids || []}
                        />
                    )}
                </div>
            )}

            {view === "schedule" && tournament?.id && (
                <div data-testid="pane-schedule">
                    <ScheduleWidget
                        tournamentId={tournament.id}
                        tournament={tournament}
                        isAdmin={!!me?.is_admin}
                        leagueId={leagueId}
                        teams={tournament.teams || []}
                        onTeamRenamed={onTeamRenamed}
                        template={tournament?.template}
                        isSpectator={isSpectator}
                        myTeamIds={me?.assigned_team_ids || []}
                    />
                </div>
            )}

            {view === "game-progress" && tournament?.id && (
                <div data-testid="pane-game-progress">
                    <SquaresGameProgress tournamentId={tournament.id} meId={me?.id} />
                </div>
            )}

            {/* iter87.48 — Legacy `intel` pane retired. The old
                Rivalry Intel modal (Your Standing gauge · Biggest
                Movers · Eliminated) was replaced by the new Rivalries
                Intel v2 chip in the Draft section (see TodoChips), so
                this render branch and its `LeagueIntelStrip` component
                are no longer wired to any tile. */}

            {view === "teams" && (
                <div data-testid="pane-teams">
                    <MyTeamsGrid
                        teams={myTeams}
                        breakdown={myRow?.team_breakdown}
                        isSeason={(tournament?.format || "").toLowerCase() === "round_robin"}
                        isNfl={tournament?.template === "nfl_season_api"}
                        tournament={tournament}
                    />
                </div>
            )}
        </Takeover>
    );
}
